import * as dayjs from 'dayjs';
import { TimeZone } from '../../../time/timezone.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../dto/dto.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { OpenAccessPass } from './open-access-pass.js';

export enum OpenAccessPassRenewalRequestStatus {
  Pending = 'PENDING',
  Completed = 'COMPLETED'
}

export class OpenAccessPassRenewalRequest extends DataModel {
  static readonly TYPE_ID: string = '3712044a104d42ef8b333e0a111694b5';

  private constructor(
    public readonly uniqueId: string,
    public readonly orderId: string,
    public readonly pass: OpenAccessPass,
    public readonly creationDate: dayjs.Dayjs,
    public readonly renewalDate: dayjs.Dayjs,
    public readonly status: OpenAccessPassRenewalRequestStatus,
  ) {
    super();
  }

  get $typeId(): string {
    return OpenAccessPassRenewalRequest.TYPE_ID;
  }

  static fromPass(opts: {
      uniqueId: string,
      orderId: string,
      pass: OpenAccessPass,
      creationDate: dayjs.Dayjs,
      renewalDate: dayjs.Dayjs,
  }) {
    return new OpenAccessPassRenewalRequest(
        opts.uniqueId,
        opts.orderId,
        opts.pass,
        opts.creationDate,
        opts.renewalDate,
        OpenAccessPassRenewalRequestStatus.Pending,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassRenewalRequest {
    return new OpenAccessPassRenewalRequest(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'orderId'),
        context.fromData<OpenAccessPass>(MapUtils.get<SerializedData>(map, 'pass')),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.decodeDate(MapUtils.get<number>(map, 'renewalDate')),
        toEnumFromString(MapUtils.get<string>(map, '$status'), OpenAccessPassRenewalRequestStatus),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'orderId': this.orderId,
      'pass': context.toData(this.pass),
      'passId': this.pass.uniqueId,
      'passTemplateId': this.pass.templateId,
      'businessRef': this.pass.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'renewalDate': context.encodeDate(this.renewalDate),
      '$status': fromEnumAsString(this.status),
    };
  }

  clone(): this {
    return new OpenAccessPassRenewalRequest(
        this.uniqueId,
        this.orderId,
        this.pass.clone(),
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(this.renewalDate),
        this.status,
    ) as this;
  }

  isSame(other: OpenAccessPassRenewalRequest): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.orderId === this.orderId &&
        other.creationDate === this.creationDate &&
        other.renewalDate === this.renewalDate &&
        other.status === this.status &&
        isSameData(other.pass, this.pass);
  }

  copyWith(data: Partial<OpenAccessPassRenewalRequest>): this {
    return new OpenAccessPassRenewalRequest(
        data.uniqueId ?? this.uniqueId,
        data.orderId ?? this.orderId,
        data.pass ?? this.pass.clone(),
        data.creationDate ?? TimeZone.cloneDate(this.creationDate),
        data.renewalDate ?? TimeZone.cloneDate(this.renewalDate),
        data.status ?? this.status,
    ) as this;
  }
}

export class OpenAccessPassRenewalRequestBuilder extends DataModelBuilder<OpenAccessPassRenewalRequest> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassRenewalRequest {
    return OpenAccessPassRenewalRequest.fromMap(context, map);
  }
}
